import { signInAnonymously, signOut } from 'firebase/auth';
import { collection, doc, setDoc, getDocs, writeBatch, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { DEMO_TEAM_TEMPLATE, DEMO_PLAYERS_TEMPLATE, DEMO_GAMES_TEMPLATE } from './demo-data';
import { PITCH_COUNT_RULESETS, calculateRestDays } from './pitch-count-rules';
import {
  WESTLAKE_KEY,
  DEMO_ATBATS,
  WESTLAKE_INSIGHTS,
  WESTLAKE_OPPONENT_RECORD,
} from './demo-intel';

export const DEMO_SESSION_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours

// Firestore caps a write batch at 500 ops. The demo seeds ~350 docs; keep a margin
// and commit in chunks so the seed survives even if the dataset grows later.
const BATCH_LIMIT = 450;

/** Commit a list of (ref, data) writes in chunks under the Firestore batch cap. */
async function commitChunked(writes: Array<{ ref: any; data: any }>): Promise<void> {
  for (let i = 0; i < writes.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const w of writes.slice(i, i + BATCH_LIMIT)) batch.set(w.ref, w.data);
    await batch.commit();
  }
}

export async function startDemoSession(): Promise<void> {
  const { user } = await signInAnonymously(auth);
  const uid = user.uid;

  const expiresAt = new Date(Date.now() + DEMO_SESSION_DURATION_MS).toISOString();
  const teamRef = doc(collection(db, 'teams'));
  const teamId = teamRef.id;

  // Write the team doc first — player rules call isCoachOfTeam() which does a
  // get() on this doc, so it must exist before the subcollection batch runs.
  await setDoc(teamRef, {
    ...DEMO_TEAM_TEMPLATE,
    coachId: uid,
    coaches: [uid],
    isDemoSession: true,
    expiresAt,
    createdAt: new Date().toISOString(),
  });

  // Collect every subcollection write, then commit in chunks under the batch cap.
  const writes: Array<{ ref: any; data: any }> = [];

  // The ace's fresh outing lands the day before the Westlake rematch (which is +4
  // days below), so the bullpen shows him on rest at that game. Little League 12U
  // is pitch-based (85/day): 78 pitches falls in the 66–85 bucket = 4 days rest
  // required, so throwing the day before emphatically blocks him for the rematch.
  const aceOutingDay = new Date();
  aceOutingDay.setDate(aceOutingDay.getDate() + 3);
  aceOutingDay.setHours(0, 0, 0, 0);
  const aceOutingDate = `${aceOutingDay.getFullYear()}-${String(aceOutingDay.getMonth() + 1).padStart(2, '0')}-${String(aceOutingDay.getDate()).padStart(2, '0')}`;

  // The threshold-rule engine (Little League) reads a precomputed `requiredRestDays`
  // + `eligibleDate` off each log entry — the same fields the live game writes when
  // an outing is logged. Compute them here so the demo's ace is genuinely on rest,
  // not just visually. 78 pitches @ LL 12U → 4 rest days → eligible 5 days later.
  const aceRuleset = PITCH_COUNT_RULESETS[DEMO_TEAM_TEMPLATE.settings.pitchCountRuleset];
  const aceAgeRules = aceRuleset?.ageGroups?.[DEMO_TEAM_TEMPLATE.settings.pitchCountAgeGroup] || aceRuleset?.ageGroups?.['12U'];
  const aceRestDays = calculateRestDays(78, aceAgeRules?.restThresholds || []);
  const aceEligibleDay = new Date(aceOutingDay);
  aceEligibleDay.setDate(aceEligibleDay.getDate() + aceRestDays + 1);
  aceEligibleDay.setHours(0, 0, 0, 0);

  for (const player of DEMO_PLAYERS_TEMPLATE) {
    const { id: playerId, ...playerData } = player;
    if (playerId === 'dp_thompson') {
      playerData.pitchingLog = [
        ...(playerData.pitchingLog ?? []),
        {
          date: aceOutingDay.toISOString(),
          pitchCount: 78,
          requiredRestDays: aceRestDays,
          eligibleDate: aceEligibleDay.toISOString(),
          inningsPitched: 5,
          gameId: 'g11',
        },
      ] as any;
    }
    const playerRef = doc(db, `teams/${teamId}/players`, playerId);
    writes.push({ ref: playerRef, data: { ...playerData, coachId: uid } });
  }

  // The scheduled games carry fixed template dates that drift into the past as
  // real time passes. The demo's whole story is scouting an UPCOMING rival game,
  // so re-date the scheduled games relative to "now" at session start. The
  // Westlake rematch (g13) is the soonest, so it's the "next game" on Home and the
  // game the coach scouts. Completed games keep their fixed historical dates.
  const futureDate = (daysFromNow: number, time: string): string => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${time}`; // matches the template's local "YYYY-MM-DDTHH:mm"
  };
  const SCHEDULED_OFFSETS: Record<string, { days: number; time: string }> = {
    g13: { days: 4,  time: '12:00' }, // Westlake Warriors — the rematch the coach scouts
    g12: { days: 8,  time: '10:00' }, // Ridgeline Reds
    g14: { days: 11, time: '11:00' }, // Eastside Eagles
  };

  // Map each template game id (g1, g6, …) → its real Firestore doc id, so the
  // synthetic play-by-play below can be filed under the right game.
  const gameIdMap: Record<string, string> = {};
  for (const game of DEMO_GAMES_TEMPLATE) {
    const { id: templateId, ...gameData } = game;
    const gameRef = doc(collection(db, `teams/${teamId}/games`));
    gameIdMap[templateId] = gameRef.id;
    const sched = SCHEDULED_OFFSETS[templateId];
    const data = sched
      ? { ...gameData, date: futureDate(sched.days, sched.time) }
      : gameData;
    writes.push({ ref: gameRef, data: { ...data, createdAt: new Date().toISOString() } });
  }

  // ── GameChanger-pipeline seed data ──────────────────────────────────────────
  // Synthetic play-by-play (at-bats) for the season, the bench-coach insight cards
  // for the Westlake meetings, and the accumulated Westlake opponent scouting
  // record. This is what makes the demo ship with a full opposition-research story
  // — no PDF import needed. Additive only; never touches stats/xOVR/pitchingLog.
  for (const [templateId, atBats] of Object.entries(DEMO_ATBATS)) {
    const realGameId = gameIdMap[templateId];
    if (!realGameId) continue;
    for (const ab of atBats) {
      const abRef = doc(collection(db, `teams/${teamId}/games/${realGameId}/atBats`));
      writes.push({ ref: abRef, data: { ...ab, gameId: realGameId } });
    }
  }
  for (const [templateId, insights] of Object.entries(WESTLAKE_INSIGHTS)) {
    const realGameId = gameIdMap[templateId];
    if (!realGameId) continue;
    for (const ins of insights) {
      const insRef = doc(collection(db, `teams/${teamId}/games/${realGameId}/insights`));
      writes.push({ ref: insRef, data: { ...ins, gameId: realGameId } });
    }
  }
  // Opponent record: remap the template game ids to the real ids.
  const oppRef = doc(db, `teams/${teamId}/opponents`, WESTLAKE_KEY);
  writes.push({
    ref: oppRef,
    data: {
      ...WESTLAKE_OPPONENT_RECORD,
      gameIds: WESTLAKE_OPPONENT_RECORD.gameIds
        .map(id => gameIdMap[id])
        .filter(Boolean),
    },
  });

  await commitChunked(writes);
}

export async function cleanupExpiredDemoSession(teamId: string): Promise<void> {
  // Delete per-game nested subcollections (atBats, insights) first, while the
  // game docs still exist to enumerate them.
  const gamesSnap = await getDocs(collection(db, `teams/${teamId}/games`));
  for (const g of gamesSnap.docs) {
    for (const nested of ['atBats', 'insights']) {
      const nestedSnap = await getDocs(collection(db, `teams/${teamId}/games/${g.id}/${nested}`));
      if (nestedSnap.empty) continue;
      const nb = writeBatch(db);
      nestedSnap.docs.forEach(d => nb.delete(d.ref));
      await nb.commit();
    }
  }

  const SUBCOLLECTIONS = ['players', 'games', 'tournaments', 'insights', 'opponents'];
  for (const sub of SUBCOLLECTIONS) {
    const snap = await getDocs(collection(db, `teams/${teamId}/${sub}`));
    if (snap.empty) continue;
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, 'teams', teamId));
  await signOut(auth);
}

export function getDemoTimeRemaining(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const totalMin = Math.ceil(ms / 60000);
  if (totalMin >= 60) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${totalMin}m`;
}
